import { apiClient } from '@/api';
import { cn } from '@/lib/utils';
import { StickyNote } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useChatMessages } from '@chainlit/react-client';

import { Markdown } from '@/components/Markdown';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';

type SaveStatus = 'idle' | 'saving' | 'saved';
type ViewMode = 'edit' | 'preview';

const AUTOSAVE_DELAY_MS = 800;

export default function ThreadNotesButton() {
  const { threadId } = useChatMessages();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [mode, setMode] = useState<ViewMode>('edit');

  const notesRef = useRef('');
  const savedRef = useRef('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persist = useCallback(
    async (value: string) => {
      if (!threadId || value === savedRef.current) return;
      setStatus('saving');
      try {
        await apiClient.saveThreadNotes(threadId, value);
        savedRef.current = value;
        setStatus('saved');
      } catch {
        setStatus('idle');
      }
    },
    [threadId]
  );

  useEffect(() => {
    setNotes('');
    notesRef.current = '';
    savedRef.current = '';
    setStatus('idle');
    setMode('edit');
  }, [threadId]);

  useEffect(() => {
    if (!open || !threadId) return;
    let cancelled = false;
    apiClient
      .getThreadNotes(threadId)
      .then((res) => {
        if (cancelled) return;
        const value = res?.notes ?? '';
        savedRef.current = value;
        notesRef.current = value;
        setNotes(value);
      })
      .catch(() => setStatus('idle'));
    return () => {
      cancelled = true;
    };
  }, [open, threadId]);

  useEffect(() => {
    if (!open) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void persist(notes), AUTOSAVE_DELAY_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [notes, open, persist]);

  const handleChange = (value: string) => {
    notesRef.current = value;
    setNotes(value);
  };

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        setMode('edit');
      } else {
        if (timerRef.current) clearTimeout(timerRef.current);
        void persist(notesRef.current);
      }
    },
    [persist]
  );

  if (!threadId) return null;

  const modeButton = (value: ViewMode, label: string) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      className={cn(
        'rounded-[5px] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors',
        mode === value
          ? 'bg-accent text-accent-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground'
      )}
    >
      {label}
    </button>
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-no-maximize
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          className={cn(
            'flex h-5 items-center gap-1 rounded px-1.5 text-[10px] font-medium uppercase tracking-wide transition-colors',
            'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
          )}
          title="Notes for this thread"
          aria-label="Thread notes"
        >
          <StickyNote className="h-3 w-3" aria-hidden="true" />
          <span>Notes</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={6}
        className="flex h-[60vh] w-[420px] max-w-[90vw] flex-col p-0"
      >
        <div className="flex flex-none items-center gap-2 border-b border-border px-3 py-2">
          <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <StickyNote className="h-3.5 w-3.5" aria-hidden="true" />
            Thread Notes
          </span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground/70">
              {status === 'saving'
                ? 'Saving…'
                : status === 'saved'
                  ? 'Saved'
                  : ''}
            </span>
            <div className="flex items-center rounded-md bg-muted p-0.5">
              {modeButton('edit', 'Edit')}
              {modeButton('preview', 'Preview')}
            </div>
          </div>
        </div>
        {mode === 'edit' ? (
          <Textarea
            value={notes}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="Scratchpad for this thread — observations, credentials, intel. Saved automatically."
            className="min-h-0 flex-1 resize-none rounded-none border-0 bg-transparent font-mono text-xs focus-visible:ring-0 focus-visible:ring-offset-0"
          />
        ) : notes.trim() ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3 text-sm leading-relaxed">
            <Markdown>{notes}</Markdown>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 items-center justify-center px-6 text-center text-xs text-muted-foreground/70">
            Nothing to preview yet — switch to Edit and jot something down.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
